// ════════════════════════════════════════════════════════════════════
// UHC COMMISSION STATEMENT PARSER
//
// Reads UnitedHealthcare commission statements (IFP / ACA Marketplace).
// UHC's statement format is a row-per-transaction CSV / tabular extract.
// We classify each row into one of five lifecycle actions:
//
//   NEW            — first-month enrollment commission
//   PMPM           — monthly per-member-per-month renewal commission
//   CHARGEBACK     — reversed commission (the client lapsed / didn't pay)
//   REINSTATEMENT  — policy came back from cancellation
//   ADJUSTMENT     — carrier correction (rate change, true-up)
//
// The classification drives every downstream behavior:
//   - NEW / PMPM    → update health_policies.last_commission_at
//   - CHARGEBACK    → fire health_loss_events (reason='payment_stopped')
//   - REINSTATEMENT → restore policy status to active
//
// PRODUCT CODE MAPPING
// ──────────────────────────────────────────────────────────────────
// UHC labels Marketplace plans with short codes (IEXB, IEXS, IEXG,
// IEXP for Bronze / Silver / Gold / Platinum). We map them to readable
// labels using a canonical default table; the agency can override per
// owner from the Commission Intelligence settings.
// ════════════════════════════════════════════════════════════════════

// ── Detection signature ─────────────────────────────────────────
// UHC statements always carry: a commission-action column (variant
// names: 'commission action', 'transaction type', 'action'), and the
// 'UnitedHealthcare' (or 'UHC') brand string within the first 4kB.
const SIGNATURE_HEADERS = [
  /commission\s*action/i,
  /transaction\s*type/i,
  /commission\s*type/i,
  /commission\s*reason/i,
];
const SIGNATURE_BRAND = /\bUnitedHealthcare\b|\bUHC\b|\bGolden Rule\b/;

export function isUhcStatementFormat(text) {
  const head = String(text || '').slice(0, 8192);
  const hasHeader = SIGNATURE_HEADERS.some(re => re.test(head));
  const hasBrand  = SIGNATURE_BRAND.test(head);
  return hasHeader && hasBrand;
}

// ── Action classifier ──────────────────────────────────────────
// Maps UHC's free-form action strings to our 5-value enum.
// Accepts the canonical labels + the variants we've seen in the wild
// ("CB", "REVERSAL", "RECOUP", etc.).
const ACTION_PATTERNS = [
  { re: /^\s*(new|new\s*enroll|enrollment|first\s*month|initial)\b/i,             action: 'new' },
  { re: /^\s*(pmpm|monthly|renewal|recurring|per\s*member)\b/i,                   action: 'pmpm' },
  { re: /^\s*(chargeback|cb|reversal|recoup|recoupment|clawback|reversed)\b/i,    action: 'chargeback' },
  { re: /^\s*(reinstate|reinstatement|restored|reactivat)\b/i,                    action: 'reinstatement' },
  { re: /^\s*(adjust|adjustment|true.?up|correction|rate\s*change)\b/i,           action: 'adjustment' },
];
export function classifyUhcAction(raw, amount) {
  const s = String(raw || '').trim();
  for (const p of ACTION_PATTERNS) if (p.re.test(s)) return p.action;
  // Fallback: negative amount with no recognized label = chargeback.
  if (Number(amount) < 0) return 'chargeback';
  if (Number(amount) > 0 && /enroll|new/i.test(s)) return 'new';
  return 'other';
}

// ── Default UHC product code table ─────────────────────────────
// Loaded into uhc_product_codes per-owner on first use; agencies can
// override or extend from the settings panel.
export const DEFAULT_UHC_PRODUCT_CODES = {
  IEXB:  { label: 'ACA Marketplace Bronze',            metal: 'Bronze'   },
  IEXBE: { label: 'ACA Marketplace Expanded Bronze',   metal: 'Bronze'   },
  IEXS:  { label: 'ACA Marketplace Silver',            metal: 'Silver'   },
  IEXG:  { label: 'ACA Marketplace Gold',              metal: 'Gold'     },
  IEXP:  { label: 'ACA Marketplace Platinum',          metal: 'Platinum' },
  IEXC:  { label: 'ACA Marketplace Catastrophic',      metal: 'Catastrophic' },
};

export function uhcProductLabel(code, overrides) {
  if (!code) return null;
  const upper = String(code).toUpperCase().trim();
  if (overrides && overrides[upper]) return overrides[upper].label;
  return DEFAULT_UHC_PRODUCT_CODES[upper]?.label || upper;
}

// ── CSV helpers (quote-aware split + multi-line cells) ─────────
function parseCsvLine(line) {
  const out = [];
  let cur = '';
  let inQ = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQ) {
      if (ch === '"' && line[i + 1] === '"') { cur += '"'; i++; }
      else if (ch === '"') inQ = false;
      else cur += ch;
    } else {
      if (ch === ',') { out.push(cur); cur = ''; }
      else if (ch === '"') inQ = true;
      else cur += ch;
    }
  }
  out.push(cur);
  return out;
}
function splitCsvLines(text) {
  const lines = [];
  let cur = '';
  let inQ = false;
  const s = String(text || '');
  for (let i = 0; i < s.length; i++) {
    const ch = s[i];
    if (ch === '"') { inQ = !inQ; cur += ch; continue; }
    if ((ch === '\n' || ch === '\r') && !inQ) {
      if (cur) lines.push(cur);
      cur = '';
      if (ch === '\r' && s[i + 1] === '\n') i++;
      continue;
    }
    cur += ch;
  }
  if (cur) lines.push(cur);
  return lines;
}

// ── Field aliasing — UHC ships several column-name variants ────
const HEADER_ALIASES = {
  client_name:    ['client name','member name','insured name','subscriber name','name'],
  first_name:     ['first name','first','given name'],
  last_name:      ['last name','last','family name','surname'],
  member_id:      ['member id','member number','subscriber id'],
  policy_number:  ['policy number','policy #','policy no','contract number'],
  carrier:        ['carrier','issuer','insurer'],
  product_code:   ['product code','product','product id'],
  state:          ['state','st'],
  effective_date: ['effective date','eff date','effective','plan effective'],
  agent_npn:      ['agent','agent number','agent npn','agent id','npn','producer'],
  action:         ['commission action','transaction type','commission type','commission reason','action'],
  amount:         ['commission','commission amount','amount','net amount','total','payment amount'],
  paid_date:      ['paid date','statement date','payment date','process date','run date'],
};

function buildColumnMap(headerRow) {
  const map = {};
  headerRow.forEach((h, i) => {
    const norm = String(h || '').toLowerCase().trim().replace(/[._-]+/g, ' ');
    for (const [field, aliases] of Object.entries(HEADER_ALIASES)) {
      if (aliases.includes(norm) && map[field] == null) map[field] = i;
    }
  });
  return map;
}

function pickCell(cells, ix) {
  if (ix == null || ix < 0) return '';
  const v = cells[ix];
  return v == null ? '' : String(v).trim();
}

// ── Coercion ────────────────────────────────────────────────────
function parseMoney(s) {
  if (s == null || s === '') return null;
  let str = String(s).replace(/[$,\s]/g, '');
  // Parenthesized = negative ($25.00) or (25.00)
  const paren = /^\(([\d.]+)\)$/.exec(str);
  if (paren) { const n = Number(paren[1]); return Number.isFinite(n) ? -n : null; }
  const n = Number(str);
  return Number.isFinite(n) ? n : null;
}
function parseDate(s) {
  if (!s) return null;
  const str = String(s).trim();
  if (!str) return null;
  if (/^\d{4}-\d{2}-\d{2}/.test(str)) return str.slice(0, 10);
  const m = /^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/.exec(str);
  if (m) {
    const yyyy = m[3].length === 2 ? '20' + m[3] : m[3];
    const mm = String(m[1]).padStart(2, '0');
    const dd = String(m[2]).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }
  return null;
}

// ── The main parser ────────────────────────────────────────────
// Returns { rows, summary, statementDate?, carrier, sourceKind }.
export function parseUhcStatement(text) {
  const lines = splitCsvLines(text).map(l => l.trim()).filter(Boolean);
  if (lines.length < 2) return emptyResult();

  // UHC statements sometimes have a few metadata rows above the
  // actual header. Find the first row containing one of our header
  // signatures.
  let headerIx = 0;
  for (let i = 0; i < Math.min(lines.length, 20); i++) {
    if (SIGNATURE_HEADERS.some(re => re.test(lines[i]))) { headerIx = i; break; }
  }
  const header = parseCsvLine(lines[headerIx]);
  const col = buildColumnMap(header);
  if (col.action == null) return emptyResult();

  const rows = [];
  const summary = {
    rowCount: 0,
    byAction: { new: 0, pmpm: 0, chargeback: 0, reinstatement: 0, adjustment: 0, other: 0 },
    grossAmount: 0,         // sum of positive (NEW + PMPM + REINSTATE + positive ADJUST)
    chargebackAmount: 0,    // abs() of negative rows
    netAmount: 0,
    chargebackClients: [],
    newEnrollments: [],
    products: {},           // product_code → count
  };

  let statementDate = null;
  let carrierLabel = 'UnitedHealthcare';

  for (let i = headerIx + 1; i < lines.length; i++) {
    const cells = parseCsvLine(lines[i]);
    if (cells.length < 2) continue;

    const rawAction = pickCell(cells, col.action);
    const amount = parseMoney(pickCell(cells, col.amount));
    if (rawAction === '' && amount == null) continue;  // junk row

    const action = classifyUhcAction(rawAction, amount);

    // Identity
    const firstName = pickCell(cells, col.first_name);
    const lastName  = pickCell(cells, col.last_name);
    let   clientName = pickCell(cells, col.client_name);
    if (!clientName && (firstName || lastName)) {
      // UHC's "LAST, FIRST" form → flip to title-cased "First Last"
      clientName = [firstName, lastName].filter(Boolean).join(' ').trim();
    }
    // Some UHC exports include "LAST, FIRST" in a single name field.
    if (/,/.test(clientName) && !/,\s/.test(clientName)) {
      // skip — already clean
    } else if (/,/.test(clientName)) {
      const [last, first] = clientName.split(',').map(t => t.trim());
      clientName = `${first} ${last}`.trim();
    }

    const memberId      = pickCell(cells, col.member_id);
    const policyNumber  = pickCell(cells, col.policy_number);
    const productCode   = pickCell(cells, col.product_code).toUpperCase();
    const state         = pickCell(cells, col.state).toUpperCase().slice(0, 2);
    const effective     = parseDate(pickCell(cells, col.effective_date));
    const agentNpn      = pickCell(cells, col.agent_npn);
    const paidDate      = parseDate(pickCell(cells, col.paid_date));
    if (paidDate && !statementDate) statementDate = paidDate;

    const carrierRaw = pickCell(cells, col.carrier);
    if (carrierRaw) carrierLabel = carrierRaw;

    rows.push({
      _source:        'uhc',
      action,
      rawAction:      rawAction || null,
      client_name:    clientName || null,
      member_id:      memberId || null,
      policy_number:  policyNumber || null,
      carrier:        carrierLabel,
      carrier_short:  'UHC',
      product_code:   productCode || null,
      product_label:  uhcProductLabel(productCode),
      state:          state || null,
      effective_date: effective,
      agent_npn:      agentNpn || null,
      amount:         amount,
      paid_date:      paidDate,
      // Keep the raw cell map for the audit trail.
      _raw: header.reduce((acc, h, ix) => {
        const v = cells[ix];
        if (v != null && String(v).trim() !== '') acc[String(h || `col_${ix}`).trim()] = v;
        return acc;
      }, {}),
    });

    summary.rowCount++;
    summary.byAction[action] = (summary.byAction[action] || 0) + 1;
    if (amount != null) {
      if (amount >= 0) summary.grossAmount += amount;
      else             summary.chargebackAmount += Math.abs(amount);
    }
    if (productCode) summary.products[productCode] = (summary.products[productCode] || 0) + 1;

    if (action === 'chargeback' && clientName) {
      summary.chargebackClients.push({ name: clientName, member_id: memberId, amount });
    }
    if (action === 'new' && clientName) {
      summary.newEnrollments.push({ name: clientName, member_id: memberId, amount });
    }
  }

  summary.netAmount = summary.grossAmount - summary.chargebackAmount;

  return {
    rows,
    summary,
    statementDate,
    carrier:    carrierLabel,
    carrierShort: 'UHC',
    sourceKind: 'uhc_csv',
  };
}

function emptyResult() {
  return {
    rows: [], summary: {
      rowCount: 0,
      byAction: { new: 0, pmpm: 0, chargeback: 0, reinstatement: 0, adjustment: 0, other: 0 },
      grossAmount: 0, chargebackAmount: 0, netAmount: 0,
      chargebackClients: [], newEnrollments: [], products: {},
    },
    statementDate: null, carrier: null, carrierShort: 'UHC', sourceKind: 'uhc_csv',
  };
}
